import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { TenantGate } from "./auth/TenantGate";
import { useAuth } from "./auth/AuthProvider";
import { needsPasswordSetup } from "./auth/access";
import { Shell } from "./components/Shell";
import { ExternalRedirect } from "./components/ExternalRedirect";
import { AgentPage } from "./pages/AgentPage";
import { DashboardPage } from "./pages/DashboardPage";
import { FilesPage } from "./pages/FilesPage";
import { LoginPage } from "./pages/LoginPage";
import { ProjectPage } from "./pages/ProjectPage";
import { QuotesPage } from "./pages/QuotesPage";
import { PublicLandingPage } from "./pages/PublicLandingPage";
import { AdminPage, AppHomePage, AppNotFoundPage, AuthCallbackPage, MfaPage, ResetPasswordPage, SetPasswordPage, WorkspacesPage, AccessErrorPage } from "./pages/AccessPages";
import { classifyLocation, runtimeUrls } from "./config/domains";
import { WorkspaceProvider, useWorkspace } from "./data/WorkspaceProvider";
import { canOpenProject, projectTabsFor } from "./lib/permissions";

export function SessionGate({ authenticated, loading, children }: { authenticated: boolean; loading: boolean; children: ReactNode }) {
  if (loading) return <div className="app-loader"><span/><strong>POSITRONICO</strong></div>;
  if (!authenticated) return <Navigate to="/login" replace/>;
  return children;
}

function AgentIndexRedirect() {
  const { data, loading } = useWorkspace();
  if (loading || !data) return <div className="page-loading"><span/><p>Cargando agente…</p></div>;
  return <Navigate to={data.agents[0] ? `/agentes/${data.agents[0].id}` : "/"} replace/>;
}

function LegacyProjectRedirect() {
  const [params] = useSearchParams();
  const legacy = params.get("tab");
  const target = legacy === "work" ? "pendientes" : legacy === "meetings" ? "reuniones" : legacy === "decisions" ? "decisiones" : legacy === "changes" ? "cambios" : legacy === "members" ? "miembros" : "roadmap";
  const search = params.get("search");
  return <Navigate to={`/proyecto/${target}${search ? `?search=${encodeURIComponent(search)}` : ""}`} replace/>;
}

function ProjectRoute({ tab }: { tab: string }) {
  const { data, loading } = useWorkspace();
  if (loading) return <div className="page-loading"><span/><p>Validando permisos…</p></div>;
  if (!data || !canOpenProject(data.viewer.role) || !projectTabsFor(data.viewer.role).some((allowed) => allowed === tab))
    return <AccessErrorPage code="403" title="Proyecto restringido" message="Esta sección no está disponible para tu rol."/>;
  return <ProjectPage/>;
}

function QuoteRoute() {
  const { data, loading } = useWorkspace();
  if (loading) return <div className="page-loading"><span/><p>Validando permisos…</p></div>;
  return data?.viewer.role === "CLIENT_MEMBER" ? <AccessErrorPage code="403" title="Acceso restringido" message="Las cotizaciones sólo están disponibles para el Owner y el administrador del cliente."/> : <QuotesPage/>;
}

function PasswordSetupGate({ children }: { children: ReactNode }) {
  const { user, loading, accessContext } = useAuth();
  if (loading) return <div className="app-loader"><span/><strong>POSITRONICO</strong></div>;
  if (user && needsPasswordSetup(accessContext)) return <Navigate to="/set-password" replace/>;
  return children;
}

function TenantApplication({ slug }: { slug: string }) {
  return <TenantGate slug={slug}><WorkspaceProvider><Shell><Routes>
    <Route path="/" element={<DashboardPage/>}/>
    <Route path="/proyecto" element={<Navigate to="/proyecto/roadmap" replace/>}/>
    <Route path="/proyecto/roadmap" element={<ProjectRoute tab="roadmap"/>}/>
    <Route path="/proyecto/pendientes" element={<ProjectRoute tab="pending"/>}/>
    <Route path="/proyecto/desarrollo" element={<ProjectRoute tab="development"/>}/>
    <Route path="/proyecto/reuniones" element={<ProjectRoute tab="meetings"/>}/>
    <Route path="/proyecto/decisiones" element={<ProjectRoute tab="decisions"/>}/>
    <Route path="/proyecto/cambios" element={<ProjectRoute tab="changes"/>}/>
    <Route path="/proyecto/miembros" element={<ProjectRoute tab="members"/>}/>
    <Route path="/archivos" element={<FilesPage/>}/>
    <Route path="/cotizaciones" element={<QuoteRoute/>}/>
    <Route path="/cotizaciones/:quoteId" element={<QuoteRoute/>}/>
    <Route path="/agentes" element={<AgentIndexRedirect/>}/>
    <Route path="/agentes/:agentId" element={<AgentPage/>}/>
    <Route path="/dashboard" element={<Navigate to="/" replace/>}/>
    <Route path="/project" element={<LegacyProjectRedirect/>}/>
    <Route path="/files" element={<Navigate to="/archivos" replace/>}/>
    <Route path="/quotes" element={<Navigate to="/cotizaciones" replace/>}/>
    <Route path="/agents/:agentId" element={<AgentPage/>}/>
    <Route path="*" element={<AccessErrorPage code="404" title="Página no encontrada" message="Esta ruta no existe dentro del portal."/>}/>
  </Routes></Shell></WorkspaceProvider></TenantGate>;
}

function CentralApplication() {
  return <Routes>
    <Route path="/" element={<AppHomePage/>}/>
    <Route path="/login" element={<LoginPage/>}/>
    <Route path="/forgot-password" element={<LoginPage/>}/>
    <Route path="/set-password" element={<SetPasswordPage/>}/>
    <Route path="/reset-password" element={<ResetPasswordPage/>}/>
    <Route path="/auth/callback" element={<AuthCallbackPage/>}/>
    <Route path="/mfa" element={<PasswordSetupGate><MfaPage/></PasswordSetupGate>}/>
    <Route path="/workspaces" element={<PasswordSetupGate><WorkspacesPage/></PasswordSetupGate>}/>
    <Route path="/admin/*" element={<PasswordSetupGate><AdminPage/></PasswordSetupGate>}/>
    <Route path="*" element={<AppNotFoundPage/>}/>
  </Routes>;
}

export function App() {
  const surface = classifyLocation(window.location);
  const title = surface.kind === "public" ? "POSITRONICO | Decisiones más claras" : surface.kind === "app" ? "POSITRONICO | Acceso" : surface.kind === "tenant" ? `POSITRONICO | ${surface.slug.toUpperCase()}` : "POSITRONICO";
  useEffect(() => { document.title = title; }, [title]);
  if (surface.kind === "public") return <Routes><Route path="*" element={<PublicLandingPage/>}/></Routes>;
  if (surface.kind === "app") return <CentralApplication/>;
  if (surface.kind === "tenant") return <TenantApplication slug={surface.slug}/>;
  if (surface.kind === "reserved" && surface.subdomain === "www") return <ExternalRedirect to={runtimeUrls().publicUrl}/>;
  return <AccessErrorPage code="404" title="Dominio no disponible" message="La dirección solicitada no corresponde a una superficie pública de POSITRONICO."/>;
}
